import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import OptimizedImage from '@/components/OptimizedImage';

// Keep the originals for high-density displays and offer smaller WebP files
// for narrower screens. Sort by filename to preserve the gallery order.
const originals = import.meta.glob('../../assets/home/office/office-*.webp', { eager: true, import: 'default' });
const responsive = import.meta.glob('../../assets/home/office-responsive/*.webp', { eager: true, import: 'default' });
const officePhotos = Object.entries(originals).sort(([a], [b]) => a.localeCompare(b)).map(([path, src]) => {
  const name = path.split('/').pop().replace('.webp', '');
  return { src, srcSet: `${responsive[`../../assets/home/office-responsive/${name}-480.webp`]} 480w, ${responsive[`../../assets/home/office-responsive/${name}-768.webp`]} 768w, ${src} 909w` };
});

export default function ImplantSolutionsSection() {
  const [photoIndex, setPhotoIndex] = useState(0);
  // Only the visible slide and its two neighbours are mounted, so a visitor
  // downloads one or three photos instead of all six at once; the neighbours
  // are what the 500 ms crossfade needs to be seamless in either direction.
  const mountedSlides = new Set([
    photoIndex,
    (photoIndex + 1) % officePhotos.length,
    (photoIndex - 1 + officePhotos.length) % officePhotos.length,
  ]);

  const prevPhoto = () => setPhotoIndex(i => (i - 1 + officePhotos.length) % officePhotos.length);
  const nextPhoto = () => setPhotoIndex(i => (i + 1) % officePhotos.length);

  const solutions = [
    {
      h3: "Full Mouth Reconstruction",
      description: "Explore coordinated treatment for multiple worn, damaged or missing teeth. Your individual plan may restore suitable natural teeth, replace missing teeth or combine approaches, with stages, fees and maintenance explained.",
      link: "/full-mouth-restoration"
    },
    {
      h3: "All-on-4 Dental Implants",
      description: "Our All-on-4 dental implant technique provides a complete arch of fixed teeth supported by just four strategically placed implants. It's a proven, cost-effective solution for patients seeking a same-day smile transformation.",
      link: "/all-on-4-trinity-fl"
    },
    {
      h3: "Fixed Implant Teeth & Bridges",
      description: "Explore fixed teeth and bridges supported by dental implants. These restorations stay in place for daily use and are removed only by your dental team when needed. Trinity Dental Arts does not offer removable snap-in dentures.",
      link: "/dental-bridges"
    },
    {
      h3: "Same Day Dental Implants",
      description: "With our advanced digital planning and in-house technology, many patients can receive same day dental implants — walking in with missing teeth and leaving with a brand-new smile in a single visit.",
      link: "/dental-implants"
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-white to-amber-50/30">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="max-w-5xl mx-auto text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200/50 rounded-full mb-6"
          >
            <span className="text-sm font-medium text-amber-800">Top-Rated Implant Dentist</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-3xl sm:text-4xl lg:text-5xl font-light text-slate-800 mb-4"
          >
            Your Local Dental Implant Center in <span className="font-serif italic text-amber-700">Trinity, FL</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg text-slate-600 max-w-3xl mx-auto"
          >
            As a top-rated implant dentist serving Trinity and surrounding communities, Dr. Gjelaj and the Trinity Dental Arts team offer a full range of advanced implant solutions — all under one roof.
          </motion.p>
        </div>

        {/* Office Photo Gallery */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="max-w-3xl mx-auto mb-14"
        >
          <div className="relative rounded-2xl overflow-hidden shadow-xl aspect-[4/3] sm:aspect-[16/7] bg-slate-200">
            {officePhotos.map((photo, i) => mountedSlides.has(i) && (
              <div
                key={photo.src}
                className={`absolute inset-0 transition-opacity duration-500 ${i === photoIndex ? 'opacity-100' : 'opacity-0'}`}
                aria-hidden={i !== photoIndex}
              >
                <OptimizedImage
                  src={photo.src}
                  srcSet={photo.srcSet}
                  alt={`Trinity Dental Arts office photo ${i + 1}`}
                  fill
                  width={1024}
                  height={683}
                  quality="medium"
                  className="w-full h-full object-cover"
                  sizes="(max-width: 768px) 100vw, 768px"
                />
              </div>
            ))}
            <button
              onClick={prevPhoto}
              className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-slate-800 p-2 rounded-full z-10 transition-all"
              aria-label="Previous photo"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={nextPhoto}
              className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-slate-800 p-2 rounded-full z-10 transition-all"
              aria-label="Next photo"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
              {officePhotos.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setPhotoIndex(i)}
                  className={`h-1.5 rounded-full transition-all ${i === photoIndex ? 'bg-white w-5' : 'bg-white/50 w-1.5'}`}
                  aria-label={`Photo ${i + 1}`}
                />
              ))}
            </div>
          </div>
          <p className="text-center text-xs text-slate-400 mt-2">Our state-of-the-art office in Trinity, FL</p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {solutions.map((solution, index) => (
            <motion.div
              key={solution.h3}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <Link to={solution.link}>
                <Card className="h-full hover:shadow-lg transition-all border-l-4 border-l-amber-600 group cursor-pointer">
                  <CardContent className="p-6 flex flex-col h-full">
                    <h3 className="text-xl font-medium text-slate-800 mb-3 group-hover:text-amber-700 transition-colors">
                      {solution.h3}
                    </h3>
                    <p className="text-slate-600 leading-relaxed mb-4 flex-grow">
                      {solution.description}
                    </p>
                    <div className="flex items-center gap-2 text-amber-600 font-medium group-hover:gap-3 transition-all">
                      Learn More
                      <ChevronRight className="w-5 h-5" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}